import React, { useState, useEffect, useContext } from 'react';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Container, createMuiTheme, Toolbar, Fab, Typography, CssBaseline, Tooltip, Snackbar, FormControl, InputLabel, Select, Divider, IconButton, AppBar } from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import { ThemeProvider } from '@material-ui/styles';
import { makeStyles } from '@material-ui/core/styles';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import MuiAlert from '@material-ui/lab/Alert';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import ExportIcon from '@material-ui/icons/GetApp';
import 'date-fns';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import { ConvertToCurrentTimeZone } from '../header/timeFormatter';
import { HttpService } from '../../apiAuthorization/httpService';
import { GlobalTheme } from '../../layouts/theme';
import { Context } from '../contexts/siteContext';
import { Modules } from '../contexts/modulesContext';
import { Theme } from '../contexts/themeContext';
import Card from '../card/card.js';
import CardBody from '../card/cardBody.js';
import SmallMenu from '../header/smallMenu';
import StatusIcon from '../common/statusIcon';

function Alert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
};

const useStyles = makeStyles(theme => ({
    root: {
        '& > *': {
            margin: '7px 0px',
            width: '100%'
        }
    },
    button: {
        width: '15%',
        float: 'right'
    },
    error: {
        fontSize: '12px',
        fontWeight: "bold",
        color: 'red',
        marginTop: '-5px'
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#fff'
    },
    title: {
        flex: 1
    }
}));

export default function POSMultipleAssignmentsReport(props) {
    const classes = useStyles();
    const { defaultSite, sites } = useContext(Context);
    const { trainingMode } = useContext(Modules);
    const { theme } = useContext(Theme);
    const [sitesList, setSitesList] = useState([]);
    const [startDate, setStartDate] = useState(ConvertToCurrentTimeZone(new Date(), "L", null, defaultSite));
    const [endDate, setEndDate] = useState(ConvertToCurrentTimeZone(new Date(), "L", 24, defaultSite));
    const [siteId, setSiteId] = useState(0);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [backDrop, setBackDrop] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const currentTheme = theme ? createMuiTheme(GlobalTheme[theme]) : createMuiTheme(GlobalTheme[localStorage.getItem("theme")]);
    const tableHeight = window.innerHeight - 260;

    const [posMultipleAssignments, setPOSMultipleAssignments] = useState({
        columns: [
            { field: 'RequestDateString', title: 'Request Date & Time' },
            { field: 'Attendant', title: 'Attendant Name' },
            { field: 'LocationPath', title: 'Unit/Location', width: '10%' },
            { field: 'PatientName', title: 'Patient/s' },           
            { field: 'AssignedTimeString', title: 'Assigned Time' },
            { field: 'TimePickedUpString', title: 'Start Time' },
            { field: 'TimeSignedOffString', title: 'End Time' },
            { field: 'TotalHours', title: 'SA Hours' }
        ],
        data: []
    });

    const triggerSave = () => {
        props.history.push({ pathname: "/" });
    };

    const rowStyle = (rowData, rowIndex) => {
        return { backgroundColor: rowIndex % 2 === 0 ? '' : currentTheme.alternateGridRow };
    };

    const closeSuccessSnackbar = () => {
        setOpenSuccess(false);
    };

    const getPOSMultipleAssignmentsReport = () => {
        let model = {};

        model.SiteId = siteId;
        model.FilterStartDate = startDate;
        model.FilterEndDate = endDate;
        model.AccessibleSites = sitesList;

        setBackDrop(true);
        HttpService.post("/PatientSitting/GetPOSMultipleAssignmentsReport", model)
            .then((result) => {
                let data = result.data;
                setPOSMultipleAssignments({ ...posMultipleAssignments, data: data });
                setBackDrop(false);
            },
                (error) => {
                    console.log(error);
                });
    };

    const exportToExcel = () => {
        let model = {};
        var index = sitesList.findIndex(item => item.id === siteId);

        model.SiteId = siteId;
        model.FilterStartDate = startDate;
        model.FilterEndDate = endDate;
        model.TrainingMode = trainingMode;
        model.AccessibleSites = sitesList;
        model.SiteName = sitesList[index].name;

        setBackDrop(true);
        HttpService.post("/PatientSitting/POSMultipleAssignmentsReportExport", model)
            .then((result) => {
                if (result.success) {
                    let fileName = result.name;
                    HttpService.downloadFile('/Report/DownloadFile', { fileName: fileName })
                        .then((response) => {
                            const url = window.URL.createObjectURL(new Blob([response]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = fileName;
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                        });
                }
                else {
                    setSuccessMessage(result.message);
                    setOpenSuccess(true);
                }
                setBackDrop(false);
            },
                (error) => {
                    console.log(error);
                });
    };

    useEffect(() => {
        if (sitesList.length > 0) {
            getPOSMultipleAssignmentsReport();
        }
        // eslint-disable-next-line 
    }, [siteId, startDate, endDate]);

    useEffect(() => {
        let data = sites.slice();
        let index = sites.findIndex(item => item.id === 0);
        if (index === -1) {
            data.unshift({ id: 0, name: "All" });
            setSitesList(data);
        }
        // eslint-disable-next-line 
    }, []);

    useEffect(() => {
        setSiteId(defaultSite.id);
        // eslint-disable-next-line 
    }, [sitesList]);

    return (
        <React.Fragment>
            <ThemeProvider theme={currentTheme}>
                <CssBaseline />
                <Backdrop className={classes.backdrop} id="POSMultipleAssignmentsReportBackdrop" open={backDrop}>
                    <CircularProgress color="inherit" />
                </Backdrop>
                <Snackbar open={openSuccess} autoHideDuration={3000} onClose={closeSuccessSnackbar} style={{ height: "100%" }}>
                    <Alert onClose={closeSuccessSnackbar} severity="success">
                        {successMessage}
                    </Alert>
                </Snackbar>
                <Container maxWidth="xl" height="100%" className={props.styles}>
                    <AppBar style={{ background: currentTheme.primary, color: currentTheme.color }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={triggerSave} aria-label="back">
                                <KeyboardBackspaceIcon />
                            </IconButton>
                            <Grid container direction="row" alignItems="center">
                                <Grid item>
                                    <Typography variant="h6" className={classes.title}>POS Multiple Assignments Report</Typography>
                                </Grid>
                                <Grid item>
                                    <StatusIcon />
                                </Grid>
                            </Grid>
                            <Grid container justify="flex-end">
                                <Fab style={{ background: currentTheme.secondary, color: "white" }} size="small" aria-label="export" onClick={exportToExcel}>
                                    <Tooltip title="Export" arrow><ExportIcon /></Tooltip>
                                </Fab>
                                <SmallMenu modified={false} />
                            </Grid>
                        </Toolbar>
                    </AppBar>
                    <Card color="primary" style={{ height: "100%", background: currentTheme.background }}>
                        <CardBody style={{ color: currentTheme.text, marginTop: "50px" }}>
                            <div className={classes.root} autoComplete="off">
                                <Grid
                                    container
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <KeyboardDatePicker disableToolbar fullWidth autoOk
                                                variant="inline"
                                                inputVariant="outlined"
                                                id="StartDate"
                                                label="Start Date"
                                                format="MM/dd/yyyy"
                                                size="small"
                                                value={startDate}
                                                onChange={date => setStartDate(moment(date).format("MM/DD/YYYY"))}
                                                InputAdornmentProps={{ position: "start" }}
                                                PopoverProps={{
                                                    anchorOrigin: { horizontal: "left", vertical: "bottom" },
                                                    transformOrigin: { horizontal: "left", vertical: "top" }
                                                }}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <KeyboardDatePicker disableToolbar fullWidth autoOk
                                                variant="inline"
                                                inputVariant="outlined"
                                                id="EndDate"
                                                label="End Date"
                                                format="MM/dd/yyyy"
                                                size="small"
                                                minDate={startDate}
                                                value={endDate}
                                                onChange={date => setEndDate(moment(date).format("MM/DD/YYYY"))}
                                                InputAdornmentProps={{ position: "start" }}
                                                PopoverProps={{
                                                    anchorOrigin: { horizontal: "left", vertical: "bottom" },
                                                    transformOrigin: { horizontal: "left", vertical: "top" }
                                                }}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </Grid>
                                </Grid>
                                <FormControl variant="outlined" margin="dense" className={classes.ddl}>
                                    <InputLabel htmlFor="Site">Site</InputLabel>
                                    <Select
                                        native
                                        label="Site"
                                        size="small"
                                        value={siteId}
                                        onChange={(e) => { setSiteId(parseInt(e.target.value)); }}
                                    >
                                        {
                                            sitesList.map((option, key) => {
                                                return <option key={option.id} value={option.id} label={option.name} >{option.name}</option>;
                                            })
                                        }
                                    </Select>
                                </FormControl>
                            </div>
                            <Divider style={{ height: "1px", backgroundColor: currentTheme.secondary, marginTop: "5px", marginBottom: "10px" }} />
                            <div style={{ maxWidth: '100%' }}>
                                <MaterialTable
                                    columns={posMultipleAssignments.columns}
                                    data={posMultipleAssignments.data}
                                    components={{
                                        Toolbar: (props) => (
                                            <div style={{ height: "0px" }}>
                                                <MTableToolbar {...props} />
                                            </div>
                                        )
                                    }}
                                    style={{ backgroundColor: currentTheme.background, border: "1px solid white" }}
                                    options={{
                                        filtering: false,
                                        search: false,
                                        paging: false,
                                        actionsColumnIndex: -1,
                                        minBodyHeight: tableHeight,
                                        maxBodyHeight: tableHeight,
                                        headerStyle: { fontWeight: "bold", color: currentTheme.headerColor, backgroundColor: currentTheme.headerBackground },
                                        actionsCellStyle: { color: currentTheme.secondary, hover: "white" },
                                        rowStyle: rowStyle
                                    }}
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </ThemeProvider>
        </React.Fragment>
    )
}
